import { useField } from 'remix-validated-form'
import ComboAsync, { ComboboxAsyncProps } from 'app/components/ui/combo/combo-async'
import FormInputWrapper, { FormInputWrapperProps } from './FormInputWrapper'
import { OptionType } from 'types/OptionType'

export type AsyncComboboxProps = FormInputWrapperProps<any> & ComboboxAsyncProps & { labelClassName: string }
/**
 * Combobox that receives options from api endpoint
 * @summary IMPORTANT: the input value will be the entire option object, so the validation schema that parses this input needs to refine the value like this:
 *
 * `comboVal: z.object({ value: z.string(), id: z.string(), name: z.string() }).transform((option) => ({ id: option.id, name: option.value }))`
 * This is different from FormComboAsync.tsx because it handles the whole object instead of just the value and, it doesn't use useControlField that is, this in uncontrolled component. Implementation requires to set selected value with the onChange function
 * @param {AsyncComboboxProps} props - queryURL needs to be the path to an api route i.e. /api/locations
 */
export default function FormComboAsyncV2({
  name,
  queryURL,
  fetcher,
  label,
  cols,
  wrapperClassName,
  labelClassName,
  inputClassName,
  newLineError,
  onChange,
  optionsMapper,
  showSearchInDropdown,
  value,
  useSingleFieldInput = false,
  customIcon,
  labelChildren,
  onDataLoaded,
  ...props
}: Omit<AsyncComboboxProps, `value`> & { value: OptionType | undefined; customIcon?: React.ReactNode }) {
  const { error, setTouched } = useField(name)
  const changeHandler = (val) => {
    setTouched(val.id !== (value?.id ?? ``))
    onChange?.(val)
  }
  // Object mode (`useSingleFieldInput === false`) used to get its form entries for
  // free: the combobox was Headless UI's, which serializes an object `value` into
  // `name[key]` hidden inputs. The migration to the Radix combobox (#680) dropped
  // that and nothing replaced it, so object-valued fields submitted nothing at all
  // and their schemas failed with "expected object, received undefined". Emit the
  // entries the schemas parse; `useSingleFieldInput` still renders the flat input.
  const serializeObjectValue = !useSingleFieldInput && !props.multiple && !!value
  return (
    <>
      {serializeObjectValue && (
        <>
          <input type="hidden" name={`${name}[id]`} value={value.id ?? ``} />
          <input type="hidden" name={`${name}[name]`} value={value.name ?? ``} />
          <input type="hidden" name={`${name}[value]`} value={value.value ?? ``} />
        </>
      )}
      <FormInputWrapper
        {...{
          name,
          label,
          error,
          newLineError,
          cols,
          wrapperClassName,
          className: labelClassName,
          infoTooltip: props.infoTooltip,
          labelChildren,
        }}
      >
        <ComboAsync
          {...props}
          showSearchInDropdown={showSearchInDropdown}
          name={name}
          inputClassName={inputClassName}
          queryURL={queryURL}
          fetcher={fetcher}
          optionsMapper={optionsMapper}
          onChange={changeHandler}
          value={value}
          useSingleFieldInput={useSingleFieldInput}
          allowDeselectOption={props.allowDeselectOption}
          customIcon={customIcon}
          onDataLoaded={onDataLoaded}
          prefetch={props.prefetch}
        />
      </FormInputWrapper>
    </>
  )
}
